import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import {
  FaPrint,
  FaDownload,
  FaEye,
  FaEnvelope,
  FaIndustry,
  FaUsers,
  FaSpinner,
  FaLink,
  FaCopy,
  FaPaperPlane,
} from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../../services/axios";
import jsPDF from "jspdf";
import "jspdf-autotable";

function labelGrupoSelecao(tipo) {
  if (tipo === "mais_quero" || tipo === "mais_quero_conversar") {
    return "Agendamento Prioritário";
  }
  if (tipo === "quero" || tipo === "quero_conversar") {
    return "Agendamento Desejável";
  }
  return "Talvez conversar";
}

function tagGrupoSelecao(tipo) {
  if (tipo === "mais_quero" || tipo === "mais_quero_conversar") {
    return "apf-selecao-tag apf-selecao-tag--prioritario";
  }
  if (tipo === "quero" || tipo === "quero_conversar") {
    return "apf-selecao-tag apf-selecao-tag--desejavel";
  }
  return "apf-selecao-tag apf-selecao-tag--talvez";
}

function urlAbsoluta(urlBruta) {
  const u = String(urlBruta || "").trim();
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/") && typeof window !== "undefined") {
    return `${window.location.origin}${u}`;
  }
  return u;
}

/**
 * Painel de lista de seleção do patrocinador (design system apl-).
 * Usado na guia do formulário admin e na rota standalone.
 */
const ApfListaSelecaoPanel = ({
  idPatrocinador,
  patrocinadorResumo = null,
  reloadKey = 0,
  onEnviarSelecao = null,
  isLoadingEnvio = false,
  participantes = [],
}) => {
  const [patrocinador, setPatrocinador] = useState(patrocinadorResumo);
  const [selecoes, setSelecoes] = useState([]);
  const [linkSelecao, setLinkSelecao] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showModalEnvio, setShowModalEnvio] = useState(false);
  const [idParticipanteEnvio, setIdParticipanteEnvio] = useState("");

  const participantesComEmail = useMemo(
    () =>
      (Array.isArray(participantes) ? participantes : []).filter((p) =>
        String(p?.email || "").trim()
      ),
    [participantes]
  );

  const resumoKey = useMemo(
    () =>
      patrocinadorResumo
        ? [
            patrocinadorResumo.patrocinador,
            patrocinadorResumo.email,
            patrocinadorResumo.segmento,
            patrocinadorResumo.logo,
          ].join("|")
        : "",
    [patrocinadorResumo],
  );

  useEffect(() => {
    if (patrocinadorResumo) {
      setPatrocinador(patrocinadorResumo);
    }
  }, [resumoKey, patrocinadorResumo]);

  const carregarDados = useCallback(async () => {
    if (!idPatrocinador) return;
    try {
      setIsLoading(true);
      const responseSelecoes = await api.get(
        `/patrocinador-selecoes/${idPatrocinador}`,
      );
      setSelecoes(responseSelecoes.data.escolhas || []);
      setLinkSelecao(responseSelecoes.data.linkSelecao || null);

      if (!patrocinadorResumo) {
        const responsePatrocinador = await api.get(
          `/patrocinador/${idPatrocinador}`,
        );
        setPatrocinador(responsePatrocinador.data.dados);
      }
    } catch (error) {
      console.error("Erro ao carregar lista de seleção:", error);
      toast.error("Erro ao carregar lista de seleção");
    } finally {
      setIsLoading(false);
    }
  }, [idPatrocinador, patrocinadorResumo, reloadKey]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const nomePatrocinador = patrocinador?.patrocinador || "Patrocinador";
  const segmentoExibir = String(patrocinador?.segmento || "").toUpperCase();
  const linkSelecaoAtivo = Boolean(linkSelecao?.ativo);
  const linkSelecaoExpira = linkSelecao?.expires_at
    ? new Date(linkSelecao.expires_at).toLocaleString("pt-BR")
    : null;
  const linkSelecaoUrl = urlAbsoluta(linkSelecao?.url);

  const copiarLinkSelecao = async () => {
    if (!linkSelecaoUrl) return;
    try {
      await navigator.clipboard.writeText(linkSelecaoUrl);
      toast.success("Link copiado para a área de transferência.");
    } catch {
      toast.error("Não foi possível copiar. Selecione o texto manualmente.");
    }
  };

  const imprimirLista = () => {
    const conteudo = document.getElementById("apf-tabela-selecao");
    if (!conteudo) return;
    const janela = window.open("", "_blank");
    janela.document.write(`
      <html>
        <head>
          <title>Lista de Seleção - ${nomePatrocinador}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .header { text-align: center; margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Lista de Seleção de Participantes</h1>
            <h2>${nomePatrocinador}</h2>
            <p>Data: ${new Date().toLocaleDateString("pt-BR")}</p>
          </div>
          ${conteudo.outerHTML}
        </body>
      </html>
    `);
    janela.document.close();
    janela.print();
  };

  const baixarPDF = () => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(20);
      doc.text("Lista de Seleção de Participantes", 105, 20, { align: "center" });
      doc.setFontSize(16);
      doc.text(nomePatrocinador, 105, 35, { align: "center" });
      doc.setFontSize(12);
      doc.text(`Data: ${new Date().toLocaleDateString("pt-BR")}`, 105, 45, {
        align: "center",
      });

      const dadosTabela = selecoes.map((selecao, index) => [
        index + 1,
        selecao.usuario?.nome || "—",
        selecao.usuario?.cargo || "—",
        selecao.usuario?.empresa || "—",
        labelGrupoSelecao(selecao.tipo),
      ]);

      doc.autoTable({
        startY: 60,
        head: [["#", "Nome", "Cargo", "Empresa", "Grupo"]],
        body: dadosTabela,
        theme: "grid",
        headStyles: {
          fillColor: [0, 138, 56],
          textColor: 255,
          fontSize: 12,
          fontStyle: "bold",
        },
        styles: { fontSize: 10 },
        columnStyles: {
          0: { cellWidth: 15 },
          1: { cellWidth: 60 },
          2: { cellWidth: 40 },
          3: { cellWidth: 40 },
          4: { cellWidth: 35 },
        },
      });

      doc.save(
        `lista-selecao-${nomePatrocinador.replace(/\s+/g, "-").toLowerCase()}.pdf`,
      );
    } catch (error) {
      console.error("Erro ao gerar PDF:", error);
      toast.error("Erro ao gerar PDF. Tente novamente.");
    }
  };

  if (isLoading) {
    return (
      <div className="apl-loading" aria-live="polite">
        <FaSpinner className="fa-spin" size={22} aria-hidden />
        <span>Carregando lista de seleção…</span>
      </div>
    );
  }

  return (
    <div className="apf-selecao-panel" aria-label="Lista de seleção">
      <div className="apf-selecao-resumo">
        <div className="apf-selecao-resumo-logo">
          {patrocinador?.logo ? (
            <img
              src={patrocinador.logo}
              alt=""
              className="apl-logo-thumb apf-selecao-logo"
            />
          ) : (
            <div className="apl-logo-placeholder apf-selecao-logo">—</div>
          )}
        </div>
        <div className="apf-selecao-resumo-text">
          <h3 className="apf-selecao-resumo-nome">{nomePatrocinador}</h3>
          <div className="apf-selecao-meta">
            {patrocinador?.email ? (
              <span className="apf-selecao-meta-item">
                <FaEnvelope size={12} aria-hidden />
                {patrocinador.email}
              </span>
            ) : null}
            {segmentoExibir ? (
              <span className="apf-selecao-meta-item">
                <FaIndustry size={12} aria-hidden />
                {segmentoExibir}
              </span>
            ) : null}
            <span className="apf-selecao-meta-item">
              <FaUsers size={12} aria-hidden />
              {selecoes.length}{" "}
              {selecoes.length === 1 ? "seleção" : "seleções"}
            </span>
            {linkSelecaoAtivo && linkSelecaoUrl ? (
              <button
                type="button"
                className="apf-selecao-link-badge"
                onClick={copiarLinkSelecao}
                title={
                  linkSelecaoExpira
                    ? `Clique para copiar — expira em ${linkSelecaoExpira}`
                    : "Clique para copiar o link de seleção"
                }
              >
                <FaLink size={11} aria-hidden />
                <span className="apf-selecao-link-badge-text">{linkSelecaoUrl}</span>
                <FaCopy size={11} aria-hidden />
              </button>
            ) : null}
          </div>
        </div>
        {typeof onEnviarSelecao === "function" ? (
          <div className="apf-selecao-resumo-actions">
            <button
              type="button"
              className="apl-btn apl-btn--primary"
              onClick={() => {
                if (!participantesComEmail.length) {
                  toast.error(
                    "Inclua ao menos um participante com e-mail para enviar a seleção."
                  );
                  return;
                }
                setIdParticipanteEnvio(
                  participantesComEmail.length === 1
                    ? String(participantesComEmail[0].id)
                    : ""
                );
                setShowModalEnvio(true);
              }}
              disabled={isLoadingEnvio || !idPatrocinador}
            >
              {isLoadingEnvio ? (
                <FaSpinner className="fa-spin" size={14} aria-hidden />
              ) : (
                <FaPaperPlane size={14} aria-hidden />
              )}
              {isLoadingEnvio ? "Enviando…" : "Enviar seleção"}
            </button>
          </div>
        ) : null}
      </div>

      <div className="apf-selecao-toolbar">
        <p className="apf-selecao-lead">
          Participantes escolhidos pelo patrocinador para reuniões.
        </p>
        <div className="apf-selecao-actions">
          <button
            type="button"
            className="apl-btn apl-btn--secondary"
            onClick={imprimirLista}
            disabled={!selecoes.length}
          >
            <FaPrint size={14} aria-hidden />
            Imprimir
          </button>
          <button
            type="button"
            className="apl-btn apl-btn--secondary"
            onClick={baixarPDF}
            disabled={!selecoes.length}
          >
            <FaDownload size={14} aria-hidden />
            PDF
          </button>
        </div>
      </div>

      <section
        className="apl-card apf-selecao-table-card"
        aria-label="Participantes selecionados"
      >
        {selecoes.length ? (
          <>
            <div className="apl-table-wrap">
              <table
                id="apf-tabela-selecao"
                className="apl-table apf-selecao-table"
              >
                <thead>
                  <tr>
                    <th scope="col" style={{ width: "4%" }}>
                      #
                    </th>
                    <th scope="col">Nome</th>
                    <th scope="col">Cargo</th>
                    <th scope="col">Empresa</th>
                    <th scope="col">Grupo</th>
                  </tr>
                </thead>
                <tbody>
                  {selecoes.map((selecao, index) => (
                    <tr key={selecao.id ?? index}>
                      <td>
                        <span className="apl-stat">{index + 1}</span>
                      </td>
                      <td className="apl-cell-name">
                        {selecao.usuario?.nome || "—"}
                      </td>
                      <td>{selecao.usuario?.cargo || "—"}</td>
                      <td>{selecao.usuario?.empresa || "—"}</td>
                      <td>
                        <span className={tagGrupoSelecao(selecao.tipo)}>
                          {labelGrupoSelecao(selecao.tipo)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <footer className="apl-footer">
              <span>
                Exibindo <strong>{selecoes.length}</strong>{" "}
                {selecoes.length === 1 ? "seleção" : "seleções"}
              </span>
            </footer>
          </>
        ) : (
          <div className="apl-empty">
            <FaEye size={40} className="apf-selecao-empty-icon" aria-hidden />
            <p className="apl-empty-title">Nenhuma seleção encontrada</p>
            <p className="mb-0">
              Este patrocinador ainda não fez seleções de participantes.
            </p>
          </div>
        )}
      </section>

      <Modal
        show={showModalEnvio}
        onHide={() => {
          if (isLoadingEnvio) return;
          setShowModalEnvio(false);
        }}
        centered
        className="apf-envio-selecao-modal"
      >
        <Modal.Header closeButton={!isLoadingEnvio}>
          <Modal.Title>Enviar seleção</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="apf-envio-selecao-lead">
            Escolha o participante que receberá o e-mail com o link de seleção.
          </p>
          {participantesComEmail.length ? (
            <label className="apf-envio-selecao-label">
              Destinatário
              <select
                className="form-select apf-envio-selecao-select"
                value={idParticipanteEnvio}
                onChange={(e) => setIdParticipanteEnvio(e.target.value)}
                disabled={isLoadingEnvio}
              >
                <option value="">Selecione um participante</option>
                {participantesComEmail.map((p) => {
                  const tipoLabel =
                    p.tipo === "patrocinador"
                      ? "usuário do portal"
                      : p.tipo === "participante_patrocinador"
                        ? "participante"
                        : null;
                  return (
                    <option key={p.id} value={p.id}>
                      {p.nome || "Sem nome"} — {p.email}
                      {tipoLabel ? ` (${tipoLabel})` : ""}
                    </option>
                  );
                })}
              </select>
            </label>
          ) : (
            <p className="mb-0 text-muted">
              Não há participantes com e-mail cadastrado para este patrocinador.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="apl-btn apl-btn--ghost"
            onClick={() => setShowModalEnvio(false)}
            disabled={isLoadingEnvio}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="apl-btn apl-btn--primary"
            disabled={
              isLoadingEnvio ||
              !idParticipanteEnvio ||
              !participantesComEmail.length
            }
            onClick={async () => {
              if (!idParticipanteEnvio) {
                toast.error("Selecione o participante destinatário.");
                return;
              }
              const ok = await onEnviarSelecao(Number(idParticipanteEnvio));
              if (ok !== false) {
                setShowModalEnvio(false);
                setIdParticipanteEnvio("");
              }
            }}
          >
            {isLoadingEnvio ? (
              <FaSpinner className="fa-spin" size={14} aria-hidden />
            ) : (
              <FaPaperPlane size={14} aria-hidden />
            )}
            {isLoadingEnvio ? "Enviando…" : "Enviar e-mail"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ApfListaSelecaoPanel;
